import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  EyeOff,
  KeyRound,
  Database,
  Trash2,
  ArrowRight,
} from "lucide-react";
import useSeo from "../hooks/useSeo";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";

// Long-form public security/trust page. Bilateral Calm's commitments to
// data privacy, encryption, and minimal third-party sharing — written for
// real humans first, SEO second.

const SECURITY_LD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Security & Privacy at Bilateral Calm",
  "description":
    "How Bilateral Calm protects your mental-health data. Encrypted in transit and at rest, never sold, deleted on request, minimal third-party sharing.",
  "url": "https://bilateralcalm.com/security",
  "publisher": { "@type": "Organization", "name": "Bilateral Calm" },
};

const PROMISES = [
  {
    icon: EyeOff,
    title: "We will never sell or share your data.",
    body: "Not to advertisers, not to data brokers, not to social platforms, not for AI training. The list of companies we share data with is short, public, and only ever functional (billing you, emailing you a password reset).",
  },
  {
    icon: Lock,
    title: "Encrypted in transit and at rest.",
    body: "Every byte travelling between your device and our servers is encrypted with TLS 1.2+ (the same protocol your bank uses). Anything stored on disk is encrypted at the database layer, and any sensitive free-text field is additionally encrypted with a separate per-app key — so even a database dump leaks nothing usable.",
  },
  {
    icon: KeyRound,
    title: "We never see your password — or your card.",
    body: "Passwords are hashed with bcrypt before they ever touch our database, so even our engineers can't read them. Credit cards never reach our servers at all — Stripe handles every payment, and we only store the customer ID Stripe gives back to us.",
  },
  {
    icon: Database,
    title: "Minimal data collection, by design.",
    body: "We collect the smallest amount of data needed to run the app: an email, your practice progress (so you can pick up where you left off), and your subscription state. We don't track you across the web, we don't run Facebook Pixel or Google Analytics on member pages, and we don't fingerprint your device.",
  },
  {
    icon: Trash2,
    title: "One-click deletion. Really.",
    body: "Open Settings → Privacy → Delete my account. It permanently removes your practice history, progress, favorites, reminders, sessions, and login from our servers. We retain only the legally required payment receipts, with your identifying details stripped.",
  },
  {
    icon: ShieldCheck,
    title: "Brute-force protection on every sign-in.",
    body: "After 5 failed login attempts from the same device, sign-in is locked for 15 minutes — so even leaked password lists from other sites can't be tried against your account in bulk. Optional Face ID / biometric login adds another layer your device alone controls.",
  },
];

const SUBPROCESSORS = [
  {
    name: "Stripe",
    role: "Payment processing.",
    data: "Email, name, card details (handled entirely by Stripe — never touches our servers).",
    why: "Required to process subscriptions. PCI DSS Level 1 certified.",
  },
  {
    name: "Resend",
    role: "Transactional email (password resets only).",
    data: "Your email address, the one-time reset link.",
    why: "Required to deliver password reset emails. SOC 2 Type II certified.",
  },
  {
    name: "Google (OAuth)",
    role: "Optional 'Sign in with Google'.",
    data: "Your Google email + basic profile (only if you choose Google sign-in).",
    why: "Lets you skip creating a password. Bilateral Calm requests the minimum scopes possible.",
  },
  {
    name: "MongoDB Atlas",
    role: "Database hosting.",
    data: "Your account record, practice progress, favorites.",
    why: "Encrypted at rest, IP-restricted, daily backups. SOC 2 Type II certified.",
  },
  {
    name: "RevenueCat (iOS)",
    role: "App Store subscription management.",
    data: "An anonymous app user ID linked to your Apple transaction.",
    why: "Required by Apple for subscription receipt validation on the native iOS app.",
  },
];

const SecurityPage = () => {
  const navigate = useNavigate();

  useSeo({
    title: "Security & Privacy at Bilateral Calm — Your Mental Health Data, Protected",
    description:
      "How Bilateral Calm protects your mental health data. Encrypted in transit and at rest, never sold, deleted on request, minimal third-party sharing. Read our commitments.",
    path: "/security",
  });

  return (
    <div className="min-h-screen bg-[#FBF8F1]" data-testid="security-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(SECURITY_LD) }}
      />

      <PublicHeader />

      {/* Hero */}
      <section className="px-6 sm:px-10 max-w-3xl mx-auto pt-8 pb-10 text-center">
        <p className="text-xs uppercase tracking-[0.2em] text-[#3090A8] font-medium mb-4">
          Security &amp; Privacy
        </p>
        <h1 className="font-outfit font-semibold text-4xl sm:text-5xl lg:text-6xl text-[#1B2349] tracking-tight leading-[1.05]">
          Your data is yours.
        </h1>
        <p className="mt-6 text-lg sm:text-xl text-[#4A5568] leading-relaxed">
          Bilateral Calm exists to help people regulate their nervous systems
          in private moments. The data that's created while you do that is
          some of the most personal there is. Here's exactly how we protect
          it — and what we promise never to do with it.
        </p>
      </section>

      {/* Promises grid */}
      <section className="px-6 sm:px-10 max-w-5xl mx-auto pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {PROMISES.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="bg-white rounded-2xl p-7 shadow-[0_2px_14px_rgba(0,0,0,0.04)] border border-slate-100"
              data-testid={`security-promise-${title.split(" ")[0].toLowerCase()}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#E1F2F7] flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[#3090A8]" />
              </div>
              <h2 className="font-outfit font-semibold text-xl text-[#1B2349] mb-2 leading-snug">
                {title}
              </h2>
              <p className="text-[#4A5568] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Subprocessors */}
      <section className="px-6 sm:px-10 max-w-3xl mx-auto pb-12">
        <h2 className="font-outfit font-semibold text-2xl sm:text-3xl text-[#1B2349] mt-6 mb-3">
          The complete list of companies that touch your data
        </h2>
        <p className="text-[#4A5568] leading-relaxed mb-7">
          Every modern app relies on a small handful of trusted providers for
          payments, email, and infrastructure. We list ours in full — including
          what they see, and why we use them. We will publish updates to this
          list before any new provider is added.
        </p>
        <div
          className="bg-white rounded-2xl shadow-[0_2px_14px_rgba(0,0,0,0.04)] border border-slate-100 divide-y divide-slate-100"
          data-testid="security-subprocessor-list"
        >
          {SUBPROCESSORS.map((sp) => (
            <div key={sp.name} className="p-6">
              <h3 className="font-outfit font-semibold text-lg text-[#1B2349]">
                {sp.name}
              </h3>
              <p className="text-sm text-[#3090A8] font-medium mt-0.5 mb-2">
                {sp.role}
              </p>
              <p className="text-[#4A5568] leading-relaxed text-sm">
                <strong>What they see:</strong> {sp.data}
                <br />
                <strong>Why:</strong> {sp.why}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Engineering choices */}
      <article
        className="px-6 sm:px-10 max-w-3xl mx-auto pb-16 text-[#1B2349]"
        data-testid="security-engineering"
      >
        <h2 className="font-outfit font-semibold text-2xl sm:text-3xl mt-6 mb-3">
          Some of the engineering choices behind this
        </h2>
        <div className="space-y-4 leading-relaxed text-[#4A5568]">
          <p>
            <strong>HTTPS everywhere, enforced by the browser.</strong> Every
            request to Bilateral Calm is delivered over TLS, and the server
            tells your browser to refuse insecure connections for the next
            year via the HSTS header. There is no plain-text version of
            this app.
          </p>
          <p>
            <strong>Strict security headers.</strong> Every page returned by
            our servers includes <code>X-Frame-Options: DENY</code> (no one
            can embed Bilateral Calm in a malicious iframe to trick you into
            clicking),{" "}
            <code>Referrer-Policy: strict-origin-when-cross-origin</code> (we
            don't leak the page you were on when you click outbound links),
            and a strict <code>Content-Security-Policy</code> that blocks
            scripts from anywhere we don't explicitly trust.
          </p>
          <p>
            <strong>Session cookies marked HTTP-only and Secure.</strong>{" "}
            Even if a malicious browser extension tried to read your session
            token, it can't — the cookie is invisible to JavaScript and only
            sent over HTTPS.
          </p>
          <p>
            <strong>Logs that don't leak.</strong> Our backend logs are
            written without personal identifiers like email addresses. An
            engineer triaging an issue sees opaque IDs only.
          </p>
          <p>
            <strong>Field-level encryption for sensitive notes.</strong> Any
            free-text reflection or journal entry you write inside the app is
            encrypted with a separate per-app key before being stored. Even
            our engineers cannot read those entries by querying the database.
          </p>
        </div>

        <h2 className="font-outfit font-semibold text-2xl sm:text-3xl mt-12 mb-3">
          Your rights, in one place
        </h2>
        <div className="space-y-4 leading-relaxed text-[#4A5568]">
          <p>
            <strong>Right to access.</strong> Download a complete JSON of
            everything we have stored about you, any time, from Settings →
            Privacy → "Download my data".
          </p>
          <p>
            <strong>Right to erasure.</strong> Permanently delete your
            account and all associated data with one click — Settings →
            Privacy → "Delete my account". The deletion executes immediately;
            it is not a "marked for deletion" placeholder.
          </p>
          <p>
            <strong>Right to portability.</strong> The export above is in
            standard JSON, so you can hand it to a successor service or just
            inspect it.
          </p>
          <p>
            <strong>Right to ask questions.</strong> Email{" "}
            <a
              href="mailto:mscwellness@protonmail.com"
              className="text-[#3090A8] underline underline-offset-2"
            >
              mscwellness@protonmail.com
            </a>{" "}
            and a real person at Mood and Sleep Clinic LLC — not an AI agent — will reply.
          </p>
        </div>

        <h2 className="font-outfit font-semibold text-2xl sm:text-3xl mt-12 mb-3">
          If something goes wrong
        </h2>
        <p className="text-[#4A5568] leading-relaxed">
          In the unlikely event of a data breach affecting Bilateral Calm
          users, we commit to notifying affected users by email within{" "}
          <strong>72 hours of discovery</strong>, with a clear description
          of what happened, what data was involved, and what steps you
          should take. We will publish a public post-mortem on this page
          within 30 days.
        </p>
      </article>

      {/* CTA */}
      <section className="px-6 sm:px-10 max-w-3xl mx-auto pb-20">
        <div className="bg-[#1B2349] rounded-3xl p-8 sm:p-10 text-center text-white">
          <h2 className="font-outfit font-semibold text-2xl sm:text-3xl mb-3">
            Practice in private. Always.
          </h2>
          <p className="text-white/80 leading-relaxed mb-6 max-w-xl mx-auto">
            Bilateral Calm is built for the moments most apps don't deserve
            to see. We've designed every part of it to keep those moments
            yours.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate("/pricing")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-[#1B2349] font-medium hover:bg-slate-100 transition-colors"
              data-testid="security-cta-pricing"
            >
              See plans <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/privacy")}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-white/30 text-white font-medium hover:bg-white/10 transition-colors"
              data-testid="security-cta-privacy"
            >
              Read the full privacy policy
            </button>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
};

export default SecurityPage;
